<template>
  <b-container>
    <b-row>
      <b-col md="12">
        <article class="post-area single-blog">
          <b-row>
            <b-col md="12">
              <h2>Podporniki akcije</h2>
            </b-col>
          </b-row>

          <b-row>
            <loading
              :active.sync="isLoading"
              :color="'#408521'"
              :is-full-page="false"
            ></loading>

            <b-col md="12" style="min-height: 400px">
              <!-- Toggle visibility -->
              <b-form-group class="mt-4" label="Prikaz sekcije na strani VSA">
                <b-form-checkbox
                  v-model="showSponsorsSection"
                  switch
                  size="lg"
                  @change="updateVisibility"
                >
                  {{ showSponsorsSection ? 'Sekcija je vidna' : 'Sekcija je skrita' }}
                </b-form-checkbox>
              </b-form-group>

              <hr />

              <!-- Add new sponsor button -->
              <b-button
                variant="success"
                class="mt-3 mb-4"
                @click="openAddModal"
              >
                <i class="fa fa-plus mr-1"></i>
                Dodaj novega podpornika
              </b-button>

              <!-- Sponsors list -->
              <h5 class="mt-4">Seznam podpornikov</h5>
              <p class="text-muted small">Povlecite vrstice za spremembo vrstnega reda</p>

              <draggable
                v-model="sponsors"
                handle=".drag-handle"
                @end="onDragEnd"
                tag="div"
              >
                <b-card
                  v-for="sponsor in sponsors"
                  :key="sponsor.id"
                  class="mb-2 sponsor-card"
                >
                  <b-row align-v="center">
                    <b-col cols="1" class="text-center">
                      <i class="fa fa-grip-vertical drag-handle" style="cursor: grab; color: #999;"></i>
                    </b-col>
                    <b-col cols="2">
                      <img
                        v-if="sponsor.imageUrl"
                        :src="sponsor.imageUrl"
                        :alt="sponsor.title"
                        style="max-height: 50px; max-width: 100px; object-fit: contain;"
                      />
                    </b-col>
                    <b-col cols="3">
                      <strong>{{ sponsor.title }}</strong>
                    </b-col>
                    <b-col cols="4">
                      <a v-if="sponsor.link" :href="sponsor.link" target="_blank" class="text-truncate d-block">
                        {{ sponsor.link }}
                      </a>
                      <span v-else class="text-muted">Ni povezave</span>
                    </b-col>
                    <b-col cols="2" class="text-right">
                      <b-button
                        variant="outline-primary"
                        size="sm"
                        class="mr-1"
                        @click="editSponsor(sponsor)"
                      >
                        <i class="fa fa-edit"></i>
                      </b-button>
                      <b-button
                        variant="outline-danger"
                        size="sm"
                        @click="confirmDelete(sponsor)"
                      >
                        <i class="fa fa-trash"></i>
                      </b-button>
                    </b-col>
                  </b-row>
                </b-card>
              </draggable>

              <p v-if="sponsors.length === 0" class="text-muted text-center py-4">
                Ni še dodanih podpornikov.
              </p>
            </b-col>
          </b-row>
        </article>
      </b-col>
    </b-row>

    <!-- Add/Edit Sponsor Modal -->
    <b-modal
      id="sponsorModal"
      ref="sponsorModal"
      :title="editingSponsorId ? 'Uredi podpornika' : 'Dodaj novega podpornika'"
      size="lg"
      @hidden="onModalHidden"
      hide-footer
    >
      <b-form @submit.prevent="saveSponsor">
        <b-form-group label="Naziv podpornika *" label-for="sponsorTitle">
          <b-form-input
            id="sponsorTitle"
            v-model="sponsorForm.title"
            placeholder="Naziv podpornika"
            required
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Povezava (neobvezno)" label-for="sponsorLink">
          <b-form-input
            id="sponsorLink"
            v-model="sponsorForm.link"
            placeholder="https://www.example.com"
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Logotip *" label-for="sponsorImage">
          <div v-if="sponsorForm.imageUrl || imagePreview" class="mb-2">
            <img
              :src="imagePreview || sponsorForm.imageUrl"
              alt="Preview"
              style="max-height: 80px; max-width: 200px; object-fit: contain;"
            />
            <b-button
              variant="link"
              size="sm"
              class="text-danger"
              @click="clearImage"
            >
              <i class="fa fa-times"></i> Odstrani
            </b-button>
          </div>
          <b-form-file
            v-if="!sponsorForm.imageUrl && !imagePreview"
            id="sponsorImage"
            v-model="imageFile"
            accept="image/*"
            placeholder="Izberi sliko..."
            @change="previewImage"
          ></b-form-file>
        </b-form-group>

        <div class="d-flex justify-content-end mt-4">
          <b-button
            variant="secondary"
            class="mr-2"
            @click="closeModal"
          >
            Prekliči
          </b-button>
          <b-button type="submit" variant="success" :disabled="!canSave">
            <i class="fa fa-save mr-1"></i>
            {{ editingSponsorId ? 'Shrani spremembe' : 'Dodaj podpornika' }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </b-container>
</template>

<script>
import { db } from "@/main";
import firebase from "firebase";
import Loading from "vue-loading-overlay";
import draggable from "vuedraggable";

export default {
  name: "Sponsors",
  components: {
    Loading,
    draggable
  },
  data() {
    return {
      isLoading: false,
      showSponsorsSection: true,
      sponsors: [],
      sponsorForm: {
        title: "",
        link: "",
        imageUrl: ""
      },
      imageFile: null,
      imagePreview: null,
      editingSponsorId: null,
      editingSponsorData: null,
      configId: null
    };
  },
  computed: {
    canSave() {
      return this.sponsorForm.title && (this.sponsorForm.imageUrl || this.imageFile);
    }
  },
  async mounted() {
    await this.loadConfig();
    await this.loadSponsors();
  },
  methods: {
    async loadConfig() {
      const _this = this;
      _this.isLoading = true;

      try {
        const configSnapshot = await db.collection("config").get();
        if (!configSnapshot.empty) {
          const config = configSnapshot.docs[0].data();
          _this.configId = configSnapshot.docs[0].id;
          _this.showSponsorsSection = config.showSponsorsSection !== false;
        }
      } catch (error) {
        console.error("Error loading config:", error);
      }

      _this.isLoading = false;
    },

    async loadSponsors() {
      const _this = this;
      _this.isLoading = true;

      try {
        const snapshot = await db
          .collection("sponsors")
          .get();

        _this.sponsors = snapshot.docs
          .map(doc => {
            const data = doc.data();
            return {
              ...data,
              id: doc.id  // Always use Firestore document ID, not the stored id field
            };
          })
          .sort((a, b) => (a.order || 0) - (b.order || 0));
      } catch (error) {
        console.error("Error loading sponsors:", error);
        _this.sponsors = [];
      }

      _this.isLoading = false;
    },

    async updateVisibility() {
      const _this = this;

      if (!_this.configId) return;

      try {
        await db.collection("config").doc(_this.configId).update({
          showSponsorsSection: _this.showSponsorsSection
        });
        _this.$swal("Nastavitev shranjena!", "", "success");
      } catch (error) {
        console.error("Error updating visibility:", error);
        _this.$swal("Napaka pri shranjevanju!", error.message, "error");
      }
    },

    openAddModal() {
      this.resetForm();
      this.$refs.sponsorModal.show();
    },

    closeModal() {
      this.$refs.sponsorModal.hide();
    },

    previewImage(event) {
      const file = event.target.files[0];
      if (file) {
        this.imagePreview = URL.createObjectURL(file);
        this.imageFile = file;
      }
    },

    clearImage() {
      this.imageFile = null;
      this.imagePreview = null;
      this.sponsorForm.imageUrl = "";
    },

    async uploadImage(file, sponsorId) {
      const storageRef = firebase
        .storage()
        .ref("sponsors/" + sponsorId)
        .child(file.name);

      const snapshot = await storageRef.put(file);
      return await snapshot.ref.getDownloadURL();
    },

    async saveSponsor() {
      const _this = this;
      _this.isLoading = true;

      try {
        let imageUrl = _this.sponsorForm.imageUrl;

        if (_this.editingSponsorId) {
          // Update existing sponsor
          if (_this.imageFile) {
            imageUrl = await _this.uploadImage(_this.imageFile, _this.editingSponsorId);
          }

          await db.collection("sponsors").doc(_this.editingSponsorId).set({
            id: _this.editingSponsorId,
            title: _this.sponsorForm.title,
            link: _this.sponsorForm.link || "",
            imageUrl: imageUrl,
            order: _this.editingSponsorData.order,
            createdAt: _this.editingSponsorData.createdAt || new Date(),
            updatedAt: new Date()
          }, { merge: true });

          _this.$swal("Podpornik posodobljen!", "", "success");
        } else {
          // Create new sponsor
          const newSponsorRef = db.collection("sponsors").doc();
          const sponsorId = newSponsorRef.id;

          if (_this.imageFile) {
            imageUrl = await _this.uploadImage(_this.imageFile, sponsorId);
          }

          await newSponsorRef.set({
            id: sponsorId,
            title: _this.sponsorForm.title,
            link: _this.sponsorForm.link || "",
            imageUrl: imageUrl,
            order: _this.sponsors.length,
            createdAt: new Date(),
            updatedAt: new Date()
          });

          _this.$swal("Podpornik dodan!", "", "success");
        }

        _this.closeModal();
        await _this.loadSponsors();
      } catch (error) {
        console.error("Error saving sponsor:", error);
        _this.$swal("Napaka pri shranjevanju!", error.message, "error");
      }

      _this.isLoading = false;
    },

    editSponsor(sponsor) {
      this.editingSponsorId = sponsor.id;
      this.editingSponsorData = { ...sponsor };
      this.sponsorForm = {
        title: sponsor.title,
        link: sponsor.link,
        imageUrl: sponsor.imageUrl
      };
      this.imageFile = null;
      this.imagePreview = null;
      this.$refs.sponsorModal.show();
    },

    onModalHidden() {
      // Reset form only after modal is fully hidden
      this.$nextTick(() => {
        this.resetForm();
      });
    },

    resetForm() {
      this.editingSponsorId = null;
      this.editingSponsorData = null;
      this.sponsorForm = {
        title: "",
        link: "",
        imageUrl: ""
      };
      this.imageFile = null;
      this.imagePreview = null;
    },

    confirmDelete(sponsor) {
      const _this = this;

      _this.$swal({
        title: "Ali ste prepričani?",
        text: `Ali želite izbrisati podpornika "${sponsor.title}"?`,
        icon: "warning",
        buttons: {
          cancel: {
            text: "Prekliči",
            value: null,
            visible: true
          },
          confirm: {
            text: "Izbriši",
            value: true,
            visible: true
          }
        }
      }).then(async value => {
        if (value) {
          await _this.deleteSponsor(sponsor);
        }
      });
    },

    async deleteSponsor(sponsor) {
      const _this = this;
      _this.isLoading = true;

      try {
        await db.collection("sponsors").doc(sponsor.id).delete();

        // Delete image from storage
        if (sponsor.imageUrl) {
          try {
            const imageRef = firebase.storage().refFromURL(sponsor.imageUrl);
            await imageRef.delete();
          } catch (e) {
            console.log("Could not delete image:", e);
          }
        }

        _this.$swal("Podpornik izbrisan!", "", "success");
        await _this.loadSponsors();
      } catch (error) {
        console.error("Error deleting sponsor:", error);
        _this.$swal("Napaka pri brisanju!", error.message, "error");
      }

      _this.isLoading = false;
    },

    async onDragEnd() {
      const _this = this;
      _this.isLoading = true;

      try {
        const batch = db.batch();

        _this.sponsors.forEach((sponsor, index) => {
          const ref = db.collection("sponsors").doc(sponsor.id);
          batch.update(ref, { order: index });
        });

        await batch.commit();
      } catch (error) {
        console.error("Error updating order:", error);
        _this.$swal("Napaka pri posodabljanju vrstnega reda!", error.message, "error");
      }

      _this.isLoading = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.sponsor-card {
  transition: background-color 0.2s;

  &:hover {
    background-color: #f8f9fa;
  }
}

.drag-handle:hover {
  color: #333 !important;
}
</style>
